/**
 * Delimiting one barcode scan out of the web till's global keystroke stream (ATL-1261).
 *
 * THE DEFECT THIS REPLACES
 * ------------------------
 * On web, New Sale reads a keyboard-wedge scanner through a `document` keydown listener. It
 * appended `String.fromCharCode(e.keyCode)` to a buffer and cleared that buffer ONLY on Enter,
 * then handed `addItemByBarcode` whatever had accumulated. Two things were wrong with it, both
 * measured in a real Chromium against the reporting org's data:
 *
 *   1. Every key was appended, including keys that are not characters. ArrowLeft is keyCode 37
 *      ('%'), Shift is 16 (U+0010), Tab is 9, Backspace is 8. Pressing an arrow key once on the
 *      New Sale screen left a character sitting in front of the NEXT scan, and nothing ever
 *      removed it short of an Enter.
 *   2. `String.fromCharCode(keyCode)` is not the typed character. Numpad 7 is keyCode 103, which
 *      maps to 'g' — and a wedge scanner in numpad mode sends exactly those. A letter key is
 *      always uppercase, so a lowercase alphanumeric supplier barcode could never match the
 *      catalog it was stored in.
 *
 * `addItemByBarcode` compensated for the residue by keeping the LAST 13 CHARACTERS of the
 * buffer — a guess that every barcode is EAN-13. That guess is what made the defect
 * product-specific: a 13-digit code exactly fills the window and is immune to any amount of
 * residue, while a 12-digit UPC-A is one character short and inherits one junk character, so
 * `findProductByBarcode` misses and the scan does nothing at all — no line, no message. An
 * 8-digit EAN-8 or a merchant's own short code is worse still.
 *
 * WHY A TIMING GAP IS THE RIGHT DELIMITER
 * ---------------------------------------
 * A wedge scanner does not type; it replays a decoded buffer, so its characters arrive in a
 * burst with no human-scale gap in it, and it terminates the burst with Enter. It therefore
 * delimits its own input twice, at both ends. Reading both delimiters is what removes the need
 * to guess a width — which is the actual root cause here, not the residue.
 *
 * `SCAN_BURST_GAP_MS` sits between the two populations rather than being tuned to one: a wedge
 * scanner emits well under 20ms per character (it is replaying, not waiting on a key), while
 * sustained human typing is ~120ms per character and a person switching from typing to
 * scanning pauses far longer than that. Anything slower than the threshold is treated as a new
 * input, so a stale fragment can never reach the next scan.
 *
 * This module is pure and takes `at` explicitly so the burst behaviour is testable without a
 * DOM or a fake clock — the caller passes `event.timeStamp`, which is monotonic.
 */

/**
 * Longest gap between two characters that still counts as one scanner burst.
 *
 * Below the slowest wedge scanner's inter-character time and above the fastest sustained human
 * typing. Widening it re-admits the defect (a typed fragment survives into the next scan);
 * narrowing it splits a slow scanner's burst into fragments that match nothing.
 */
export const SCAN_BURST_GAP_MS = 100

/**
 * How long the keyboard must fall silent before a burst with no Enter counts as a finished scan
 * (ATL-1263). Comfortably above `SCAN_BURST_GAP_MS`, so a scanner's own inter-character pause is
 * never mistaken for the end of its code, and short enough that the cashier sees the line appear
 * immediately.
 */
export const SCAN_COMPLETE_MS = 140

/**
 * Shortest buffer that may be completed by a pause rather than an Enter.
 *
 * Six is not arbitrary: `generateBarcode()` (utils/productValidation.js) mints six-digit internal
 * codes, so it is the shortest string that is a barcode in this system. The floor is what keeps a
 * person out of this path at all — see `completeScanOnPause`.
 */
export const MIN_AUTOCOMPLETE_LENGTH = 6

/** A buffer holding no scan. `lastAt` is -Infinity so the first keystroke always starts fresh. */
export const emptyScanState = () => ({ buffer: '', lastAt: -Infinity })

/**
 * The code a pause completed, or `null` when the buffer is not a scan.
 *
 * WHY A SCAN NEED NOT END IN ENTER
 * --------------------------------
 * A wedge scanner delimits its burst at BOTH ends: a run of characters with no human-scale gap
 * in it, and then silence. Atlas read only the Enter suffix, so a scanner configured without one
 * — a single setting in its manual, and the default on some units — had every code it sent
 * thrown away. Measured on Эко ферма's till: 20 consecutive 13-digit codes, 4ms between
 * characters, not one of them closed by Enter, not one of them added to a sale.
 *
 * WHY THIS CANNOT FIRE ON TYPING
 * ------------------------------
 * `nextScanState` restarts the buffer whenever more than `SCAN_BURST_GAP_MS` passes between two
 * characters. Sustained human typing is several times slower than that, so a typed buffer holds
 * ONE character however long the person types, and can never reach `MIN_AUTOCOMPLETE_LENGTH`.
 * A buffer that did reach it, with no internal gap above the threshold, can only have been
 * replayed by a machine. The length floor and the gap rule are two independent conditions, and
 * a person fails both.
 */
export const completeScanOnPause = (state) =>
  state.buffer.length >= MIN_AUTOCOMPLETE_LENGTH ? state.buffer : null

/**
 * Fold one keydown into the scan buffer.
 *
 * @param {{buffer: string, lastAt: number}} state  previous state; never mutated
 * @param {{key: string, at: number}} event         `event.key` and `event.timeStamp`
 * @returns {{buffer: string, lastAt: number, scanned: string|null}} `scanned` is the completed
 *   code when this keystroke terminated a scan, and `null` otherwise. An empty buffer never
 *   completes a scan, so a stray Enter — confirming a dialog, submitting a field — reports
 *   nothing rather than sending '' off to be looked up.
 */
export const nextScanState = (state, { key, at }) => {
  // A key with no `key` string at all is not a character and cannot be part of a barcode.
  if (typeof key !== 'string') {
    return { buffer: state.buffer, lastAt: state.lastAt, scanned: null }
  }

  if (key === 'Enter') {
    return { buffer: '', lastAt: state.lastAt, scanned: state.buffer.length > 0 ? state.buffer : null }
  }

  // Every non-character key — Shift, ArrowLeft, Tab, Backspace, F5, Escape, Unidentified —
  // has a multi-character `key` name. Skipping them WITHOUT touching `lastAt` is deliberate:
  // a scanner sends Shift between characters of an alphanumeric code, and treating that as a
  // keystroke would make the burst look like it had a gap in it.
  if (key.length !== 1) {
    return { buffer: state.buffer, lastAt: state.lastAt, scanned: null }
  }

  const continuesBurst = at - state.lastAt <= SCAN_BURST_GAP_MS
  return {
    buffer: (continuesBurst ? state.buffer : '') + key,
    lastAt: at,
    scanned: null,
  }
}
